import {
	faDistributeSpacingHorizontal,
	faDistributeSpacingVertical,
} from "@fortawesome/pro-solid-svg-icons";
import { Html } from "@react-three/drei";
import { Fragment } from "react";
import { match } from "ts-pattern";
import { useCabinetStore } from "../../../../context/StoreContext";
import { getDimensions } from "../../../../hooks/getDimensions";
import { LayoutModus, useCanvasStore } from "../../../../hooks/useCanvasStore";
import {
	compartmentsOnSameX,
	compartmentsOnSameY,
	getNewXValue,
	getNewYValue,
} from "../../data/Compartment";
import RoundButton from "./RoundButton";

export function Distributors() {
	const { state, api, updateCompartmentHeight, updateCompartmentWidth } = useCabinetStore();
	const { layoutMode, selectedCompartmentIds, deselectAllCompartments } = useCanvasStore();

	const compartments = selectedCompartmentIds.map((id) => state.compartments.get(id)!);

	const selectedCompartmentsOnSameY = compartments.filter(
		(c) => compartments[0] && compartmentsOnSameY(c, compartments[0]),
	);

	const selectedCompartmentsOnSameX = compartments.filter(
		(c) => compartments[0] && compartmentsOnSameX(c, compartments[0]),
	);

	const dimensionsX =
		selectedCompartmentsOnSameX.length && selectedCompartmentsOnSameX[0]
			? getDimensions(selectedCompartmentsOnSameX[0])
			: undefined;

	const dimensionsY =
		selectedCompartmentsOnSameY.length && selectedCompartmentsOnSameY[0]
			? getDimensions(selectedCompartmentsOnSameY[0])
			: undefined;

	function distributeVertically(compartmentIds: string[]) {
		const selected = compartmentIds.map((id) => state.compartments.get(id)!);

		selected.sort((a, b) => a.y - b.y);

		const totalHeight = selected.reduce((prev, current) => prev + current.height, 0);
		const newHeight = Math.floor(totalHeight / selected.length);

		let previousY = selected[0]?.y ?? 0;

		selected.forEach((compartment, index) => {
			const newY = match(index)
				.with(0, () => previousY)
				.otherwise(() => previousY + newHeight + state.thickness);

			const difference = getNewYValue(compartment, "bottom", newY - compartment.y, state, api);

			updateCompartmentHeight(compartment, "bottom", difference);

			previousY = newY;
		});

		deselectAllCompartments();
	}

	function distributeHorizontally(compartmentIds: string[]) {
		const selected = compartmentIds.map((id) => state.compartments.get(id)!);

		selected.sort((a, b) => a.x - b.x);

		const totalWidth = selected.reduce((prev, current) => prev + current.width, 0);
		const newWidth = Math.floor(totalWidth / selected.length);

		let previousX = selected[0]?.x ?? 0;

		selected.forEach((compartment, index) => {
			const newX = match(index)
				.with(0, () => previousX)
				.otherwise(() => previousX + newWidth + state.thickness);

			const difference = getNewXValue(compartment, "left", newX - compartment.x, state, api);

			updateCompartmentWidth(compartment, "left", difference);

			previousX = newX;
		});

		deselectAllCompartments();
	}

	return (
		<Fragment>
			{dimensionsX && layoutMode === LayoutModus.distributeHorizontal && (
				<>
					<Html center position={[0, dimensionsX.y, dimensionsX.z + dimensionsX.depth]}>
						<DistributeButton onClick={() => distributeHorizontally(selectedCompartmentIds)} />
					</Html>
					<Html
						center
						position={[state.width / 1000, dimensionsX.y, dimensionsX.z + dimensionsX.depth]}
					>
						<DistributeButton onClick={() => distributeHorizontally(selectedCompartmentIds)} />
					</Html>
				</>
			)}

			{dimensionsY && layoutMode === LayoutModus.distributeVertical && (
				<>
					<Html center position={[dimensionsY.x, 0, dimensionsY.z + dimensionsY.depth]}>
						<DistributeButton onClick={() => distributeVertically(selectedCompartmentIds)} />
					</Html>

					<Html
						center
						position={[dimensionsY.x, state.height / 1000, dimensionsY.z + dimensionsY.depth]}
					>
						<DistributeButton onClick={() => distributeVertically(selectedCompartmentIds)} />
					</Html>
				</>
			)}
		</Fragment>
	);
}

function DistributeButton(props: { onClick: () => void }) {
	const { layoutMode, selectedCompartmentIds } = useCanvasStore();

	const amount = selectedCompartmentIds.length;
	const minAmount = 2;
	const title =
		amount < minAmount ? `Selecteer nog ${minAmount - amount} vakken` : `Verdeel ${amount} vakken`;

	return (
		<RoundButton
			disabled={amount < minAmount}
			aria-label={title}
			onClick={(evt) => {
				evt.stopPropagation();

				props.onClick();
			}}
			icon={
				layoutMode === LayoutModus.distributeHorizontal
					? faDistributeSpacingHorizontal
					: faDistributeSpacingVertical
			}
		>
			<span className="text-xs">
				{amount}/{minAmount}
			</span>
		</RoundButton>
	);
}
